import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSsoConsume } from '../../hooks/useAuth';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import { Logo } from '../../components/brand/Logo';

/**
 * Landing page for the apace-rms SSO handoff (see AuthService.ssoConsume).
 * The whole point of the flow is that the user never sees a login form —
 * this page just burns the one-time ticket and redirects, so it's
 * deliberately minimal: a brand mark and a status line, nothing to
 * interact with.
 */
export default function SsoConsumePage() {
  const [searchParams] = useSearchParams();
  const { mutate: consume, isError } = useSsoConsume();
  const hasAttempted = useRef(false);

  useEffect(() => {
    // StrictMode/fast-refresh can mount this effect twice; a ticket is
    // single-use server-side anyway, but this avoids burning it on a
    // harmless double-invoke and getting a spurious error on the first try.
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    const ticket = searchParams.get('ticket');
    if (!ticket) {
      return;
    }
    consume(ticket);
  }, [searchParams, consume]);

  const ticket = searchParams.get('ticket');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <Logo size={24} />
          </div>
          <CardTitle className="text-2xl">
            {isError || !ticket ? 'Sign-in link expired' : 'Signing you in…'}
          </CardTitle>
          <CardDescription>
            {isError || !ticket
              ? 'This link is single-use and may have already been opened, or has expired. Please log in directly.'
              : 'Redirecting you to your dashboard.'}
          </CardDescription>
        </CardHeader>
        {(isError || !ticket) && (
          <CardContent>
            <a href="/login" className="text-sm text-primary hover:underline">
              Go to login
            </a>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
